import React, { useMemo, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, Button, Tooltip } from '@mui/material';
import ProposalCalendar from '../calendar/ProposalCalendar';
import { getPlanMonthForManagerView, getAllowedMonthStart } from '../calendar/planWeekUtils';
import StateSelect from '../common/StateSelect';
import axiosInstance from '../AxiosInstance';

const InstructorDialog = ({ open, handleClose, isEditMode, currentInstructor, handleChange, handleSave, handleInsert, handleDelete, userRole, managerId, setIsDisabled, isDisabled }) => {
  const managerInitialDate = useMemo(() => {
    const { year, month } = getPlanMonthForManagerView();
    return getAllowedMonthStart(year, month);
  }, []);

  const [refreshNonce, setRefreshNonce] = useState(0);
  const [calendarRange, setCalendarRange] = useState(null);
  const [calendarEvents, setCalendarEvents] = useState([]);

  const getEventStart = (ev) => {
    const s = ev?.start;
    if (!s) return null;
    if (typeof s === 'string') return s;
    if (s instanceof Date) return s.toISOString();
    return null;
  };

  const getEventDate = (ev) => {
    const dateStr = ev?.extendedProps?.date;
    if (dateStr) return String(dateStr).slice(0, 10);
    const s = getEventStart(ev);
    return s ? s.slice(0, 10) : '';
  };

  const getEventStartHHMM = (ev) => {
    const s = getEventStart(ev);
    if (!s) return '00:00';
    return s.length >= 16 ? s.slice(11, 16) : '00:00';
  };

  const normalizeToHHMMSS = (hhmmOrHHMMSS) => {
    const t = String(hhmmOrHHMMSS || '').trim();
    if (!t) return '00:00:00';
    if (t.length >= 8) return t.slice(0, 8);
    if (t.length >= 5) return `${t.slice(0, 5)}:00`;
    return '00:00:00';
  };

  const runWithLimit = async (tasks, limit = 5) => {
    const results = [];
    let idx = 0;
    const workers = Array.from({ length: Math.min(limit, tasks.length) }, async () => {
      while (idx < tasks.length) {
        const current = idx++;
        try {
          results[current] = { status: 'fulfilled', value: await tasks[current]() };
        } catch (e) {
          results[current] = { status: 'rejected', reason: e };
        }
      }
    });
    await Promise.all(workers);
    return results;
  };

  const handleRefreshAll = async () => {
    if (!currentInstructor?.id || !managerId) return;
    const range = calendarRange;
    const evs = Array.isArray(calendarEvents) ? calendarEvents : [];
    if (!range?.start || !range?.end) {
      setRefreshNonce((n) => n + 1);
      return;
    }

    const rangeStart = new Date(range.start);
    const rangeEnd = new Date(range.end); // exclusive
    const monthLabel = rangeStart.toLocaleString('en-US', { month: 'long', year: 'numeric' });

    const targetsInMonth = evs.filter((e) => {
      const ext = e?.extendedProps || {};
      if (ext.isSchedule || ext.isRejected) return false;
      if (ext.sourceType !== 'MANAGER' && ext.sourceType !== 'INSTRUCTOR') return false;
      if (!ext.rawId) return false;
      const d = getEventDate(e);
      if (!d) return false;
      const dt = new Date(`${d}T00:00:00Z`);
      return dt >= rangeStart && dt < rangeEnd;
    });

    if (targetsInMonth.length === 0) {
      // eslint-disable-next-line no-alert
      alert(`No Target slots found in ${monthLabel}.`);
      return;
    }

    // eslint-disable-next-line no-alert
    const ok = window.confirm(`Confirm ALL Target slots in ${monthLabel}? (count: ${targetsInMonth.length})`);
    if (!ok) return;

    const tasks = targetsInMonth.map((e) => async () => {
      const ext = e.extendedProps || {};
      const payload = {
        id: ext.rawId,
        parentId: ext.parentId || null,
        date: getEventDate(e),
        startTime: normalizeToHHMMSS(getEventStartHHMM(e)),
        endTime: normalizeToHHMMSS(ext.endTime),
      };
      return axiosInstance.post('/proposal/confirm', payload, {
        params: { instructorId: currentInstructor.id, managerId },
      });
    });

    const results = await runWithLimit(tasks, 5);
    const failed = results.filter((r) => r?.status === 'rejected');
    if (failed.length) {
      // eslint-disable-next-line no-alert
      alert(`Confirm all finished with failures: ${failed.length}/${results.length}. Please refresh and retry.`);
    } else {
      // eslint-disable-next-line no-alert
      alert(`Confirmed ${results.length} Target slots in ${monthLabel}.`);
    }
    setRefreshNonce((n) => n + 1);
  };

  const handleDialogClose = (event, reason) => {
    if (reason === "backdropClick" && isEditMode) {
      handleSave();
      return;
    }
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEditMode ? "Edit Instructor" : "Add an Instructor"}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {isEditMode ? "Edit the information of the instructor." : "Add the information of the instructor."}
        </DialogContentText>
        <TextField
          margin="dense"
          label="Id"
          type="text"
          fullWidth
          name="id"
          value={currentInstructor.id}
          onChange={handleChange}
          InputProps={{
            readOnly: isEditMode, 
          }}
        />
        <TextField
          margin="dense"
          label="FirstName"
          type="text"
          fullWidth
          name="firstname"
          value={currentInstructor.firstname}
          onChange={handleChange}
        />
        <TextField
          margin="dense"
          label="LastName"
          type="text"
          fullWidth
          name="lastname"
          value={currentInstructor.lastname}
          onChange={handleChange}
        />
        <StateSelect
          label="State"
          value={currentInstructor.state}
          onChange={(val) => handleChange({ target: { name: 'state', value: val } })}
        />
        <TextField
          margin="dense"
          label="City"
          type="text"
          fullWidth
          name="city"
          value={currentInstructor.city}
          onChange={handleChange}
        />
        <TextField
          margin="dense"
          label="PhoneNumber"
          type="text"
          fullWidth
          name="phoneNumber"
          value={currentInstructor.phoneNumber}
          onChange={handleChange}
        />
        <TextField
          margin="dense"
          label="Email"
          type="text"
          fullWidth
          name="email"
          value={currentInstructor.email}
          onChange={handleChange}
        />
        <TextField
          margin="dense"
          label="Slack ID"
          type="text"
          fullWidth
          name="slackId"
          value={currentInstructor.slackId || currentInstructor.slack_id || ''}
          onChange={handleChange}
        />
       {userRole === 'ROLE_ICPIE' && (
  <>
    <TextField
      margin="dense"
      label="Wage Per Hour"
      type="text"
      fullWidth
      name="wageHour"
      value={currentInstructor.wageHour}
      onChange={handleChange}
    />
    <TextField
      margin="dense"
      label="Total Class Times"
      type="text"
      fullWidth
      name="totalClassTimes"
      value={currentInstructor.totalClassTimes}
      onChange={handleChange}
    />
    <TextField
      margin="dense"
      label="Deposit"
      type="text"
      fullWidth
      name="deposit"
      value={currentInstructor.deposit}
      onChange={handleChange}
    />
    <TextField
      margin="dense"
      label="Rent Manikin Numbers"
      type="text"
      fullWidth
      name="rentManikinNumbers"
      value={currentInstructor.rentManikinNumbers}
      onChange={handleChange}
    />
    <TextField
      margin="dense"
      label="Finance"
      type="text"
      fullWidth
      name="finance"
      value={currentInstructor.finance}
      onChange={handleChange}
    />
    <TextField
      margin="dense"
      label="Rent Status"
      type="text"
      fullWidth
      name="rentStatus"
      value={currentInstructor.rentStatus}
      onChange={handleChange}
    />
  </>
)}
        <TextField
          margin="dense"
          label="Fob Key"
          type="text"
          fullWidth
          name="fobKey"
          value={currentInstructor.fobKey}
          onChange={handleChange}
        />
        {isEditMode && currentInstructor?.id && managerId && (
          <ProposalCalendar
            instructorId={currentInstructor.id}
            managerId={managerId}
            managerInitialDate={managerInitialDate}
            refreshNonce={refreshNonce}
            onRefresh={() => setRefreshNonce((n) => n + 1)}
            onRangeChange={(r) => setCalendarRange(r)}
            onEventsChange={(evs) => setCalendarEvents(evs)}
          />
        )}
      </DialogContent>
      <DialogActions>
        {isEditMode && (
          <Tooltip title="Caution! This will delete the instructor" enterDelay={0}>
            <Button onClick={handleDelete} color="error">
              DELETE
            </Button>
          </Tooltip>
        )}
        {isEditMode && currentInstructor?.id && managerId && (
          <Tooltip title="Confirm all target slots for this month" enterDelay={0}>
            <Button
              onClick={handleRefreshAll}
              variant="outlined"
              color="primary"
            >
              CONFIRM ALL
            </Button>
          </Tooltip>
        )}
        <Tooltip
          title="Conduct cross check with venue slots and generate new ads"
          enterDelay={0}
        >
          <span>
            <Button onClick={isEditMode ? handleSave : handleInsert} disabled={isDisabled}>
              {isEditMode ? "Save Changes" : "Add Instructor"}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default InstructorDialog;